import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Popconfirm,
  message,
  Tag,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Switch,
  Typography,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import {
  makeGetCall,
  makePostCall,
  makePutCall,
  makeDeleteCall,
  getUserDetails,
} from '../../utils/helper';
import moment from 'moment';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface ProgramListProps {
  // When provided, clicking a program row drills into its enrolled users.
  onProgramClick?: (program: any) => void;
}

const ProgramList: React.FC<ProgramListProps> = ({ onProgramClick }) => {
  const [programs, setPrograms] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchPrograms();
    fetchQuizzes();
  }, []);

  const fetchPrograms = async () => {
    setLoading(true);
    try {
      const response = await makeGetCall('/program/list');
      setPrograms(response.data.data || []);
    } catch (error) {
      message.error('Failed to fetch programs');
      console.error(error);
    }
    setLoading(false);
  };

  const fetchQuizzes = async () => {
    try {
      const response = await makeGetCall('/quiz/list');
      setQuizzes(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch quizzes for dropdown', error);
    }
  };

  const isQuizActive = (quiz) => {
    if (!quiz || !quiz.quiz_is_active) return false;
    const now = moment();
    return (
      now.isSameOrAfter(moment(quiz.quiz_start_date)) &&
      now.isSameOrBefore(moment(quiz.quiz_end_date))
    );
  };

  // Active quizzes are always selectable. When editing a program whose
  // currently-assigned quiz is no longer active, keep that quiz in the list
  // so the form still displays it (the backend skips the active-check when
  // the quiz isn't changing).
  const selectableQuizzes = (() => {
    const active = quizzes.filter(isQuizActive);
    if (!editingProgram?.program_quiz_id) return active;
    const alreadyIncluded = active.some(
      (q) => q.quiz_id === editingProgram.program_quiz_id
    );
    if (alreadyIncluded) return active;
    const assigned = quizzes.find(
      (q) => q.quiz_id === editingProgram.program_quiz_id
    );
    return assigned ? [...active, assigned] : active;
  })();

  const handleCreate = () => {
    setEditingProgram(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true });
    setIsModalVisible(true);
  };

  const handleEdit = (program) => {
    setEditingProgram(program);
    const dateRange =
      program.program_start_date && program.program_end_date
        ? [moment(program.program_start_date), moment(program.program_end_date)]
        : null;
    form.setFieldsValue({
      name: program.program_name,
      quizId: program.program_quiz_id,
      dateRange,
      isActive: program.program_is_active,
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (programId) => {
    try {
      await makeDeleteCall(`/program/${programId}`);
      message.success('Program deleted successfully');
      fetchPrograms();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to delete program');
      console.error(error);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const userDetails = getUserDetails();

      const payload: any = {
        name: values.name || null,
        quizId: values.quizId || null,
        startDate: values.dateRange ? values.dateRange[0].toISOString() : null,
        endDate: values.dateRange ? values.dateRange[1].toISOString() : null,
        isActive: values.isActive !== undefined ? values.isActive : true,
      };

      if (editingProgram) {
        await makePutCall(`/program/${editingProgram.program_id}`, payload);
        message.success('Program updated successfully');
      } else {
        payload.createdBy = userDetails?.username;
        const response = await makePostCall('/program/create', payload);
        const newId = response?.data?.data?.program_id;
        if (newId !== undefined) {
          message.success({
            content: (
              <span>
                Program created. ID:{' '}
                <Typography.Text
                  strong
                  copyable={{ text: String(newId) }}
                  style={{ color: 'inherit' }}
                >
                  {newId}
                </Typography.Text>
              </span>
            ),
            duration: 6,
          });
        } else {
          message.success('Program created successfully');
        }
      }

      setIsModalVisible(false);
      form.resetFields();
      fetchPrograms();
    } catch (error) {
      if (error.errorFields) return;
      message.error(error.response?.data?.message || 'Failed to save program');
      console.error(error);
    }
  };

  const renderStatus = (record) => {
    if (!record.program_is_active) {
      return <Tag color="default">Inactive</Tag>;
    }
    const start = record.program_start_date && moment(record.program_start_date);
    const end = record.program_end_date && moment(record.program_end_date);
    if (start && end) {
      const now = moment();
      if (now.isBefore(start)) return <Tag color="default">Upcoming</Tag>;
      if (now.isAfter(end)) return <Tag color="red">Expired</Tag>;
      return <Tag color="green">Active</Tag>;
    }
    return record.effectiveIsActive ? (
      <Tag color="green">Active</Tag>
    ) : (
      <Tag color="default">Inactive</Tag>
    );
  };

  const columns: any = [
    {
      title: 'ID',
      dataIndex: 'program_id',
      key: 'program_id',
      width: 110,
      render: (id) => (
        <Space size={4}>
          <Typography.Text strong style={{ fontFamily: 'monospace' }}>
            {id}
          </Typography.Text>
          <Tooltip title="Copy program ID">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard
                  .writeText(String(id))
                  .then(() => message.success(`Copied program ID ${id}`))
                  .catch(() => message.error('Failed to copy'));
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Program Name',
      dataIndex: 'program_name',
      key: 'program_name',
      width: '25%',
      render: (name) => name || <span style={{ color: '#999' }}>(unnamed)</span>,
    },
    {
      title: 'Quiz',
      key: 'quiz',
      width: '25%',
      render: (_, record) =>
        record.quiz?.quiz_name || (
          <span style={{ color: '#999' }}>(none)</span>
        ),
    },
    {
      title: 'Date Range',
      key: 'dateRange',
      width: '20%',
      render: (_, record) => {
        if (!record.program_start_date || !record.program_end_date) {
          return <span style={{ color: '#999' }}>—</span>;
        }
        return (
          <div>
            <div>{moment(record.program_start_date).format('DD MMM YYYY')}</div>
            <div>{moment(record.program_end_date).format('DD MMM YYYY')}</div>
          </div>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: '15%',
      render: (_, record) => renderStatus(record),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(record);
            }}
          />
          <Popconfirm
            title="Delete this program?"
            onConfirm={(e) => {
              e?.stopPropagation();
              handleDelete(record.program_id);
            }}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-4">
      <div className="mb-4 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Program Management</h2>
          <p className="text-gray-500">
            Manage enrollment programs and link them to quizzes
          </p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          Create Program
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={programs}
        loading={loading}
        rowKey="program_id"
        onRow={(record) => ({
          onClick: () => onProgramClick?.(record),
          style: onProgramClick ? { cursor: 'pointer' } : undefined,
        })}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total, range) =>
            `Showing ${range[0]}-${range[1]} of ${total} items`,
          position: ['bottomCenter'],
        }}
        scroll={{ x: 1000 }}
      />

      <Modal
        title={editingProgram ? 'Edit Program' : 'Create New Program'}
        visible={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        width={600}
      >
        <Form form={form} layout="vertical">
          {editingProgram && (
            <Form.Item label="Program ID">
              <Typography.Text
                strong
                copyable={{ text: String(editingProgram.program_id) }}
                style={{ fontFamily: 'monospace' }}
              >
                {editingProgram.program_id}
              </Typography.Text>
            </Form.Item>
          )}

          <Form.Item
            name="name"
            label="Program Name"
          >
            <Input placeholder="e.g., Chest Radiology Crash Course - June 2026" />
          </Form.Item>

          <Form.Item
            name="quizId"
            label="Associated Quiz"
          >
            <Select
              showSearch
              allowClear
              placeholder="Select a quiz (optional)"
              optionFilterProp="children"
              notFoundContent="No active quizzes available"
            >
              {selectableQuizzes.map((quiz) => {
                const active = isQuizActive(quiz);
                return (
                  <Option key={quiz.quiz_id} value={quiz.quiz_id}>
                    {quiz.quiz_name}
                    {!active && (
                      <span style={{ color: '#ff4d4f', marginLeft: 8 }}>
                        (inactive — kept from previous selection)
                      </span>
                    )}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item
            name="dateRange"
            label="Active Period (optional)"
            extra="If left blank, active status depends only on the Active toggle below."
          >
            <RangePicker style={{ width: '100%' }} showTime />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProgramList;
